import React, { forwardRef, useEffect, useState } from 'react';

import { TextField } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';

import ControllerInput from './ControllerInput';

const AutoCompleteBase = forwardRef(
  (
    {
      value,
      onChange,
      inputValue,
      updateValue,
      valueDebounce,
      label,
      loadOptions,
      fullWidth = true,
      style = {},
      autoFocus = false,
      campo = 'nome',
      getOptionSelected = (option, value) => option[campo] === value[campo],
      getOptionLabel = (option) => option[campo],
      disabled = false,
      multiple = false,
      ...rest
    },
    forwardedRef,
  ) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const loading = open && options.length === 0;

    useEffect(() => {
      if (!open) {
        setOptions([]);
        return undefined;
      }

      let active = true;

      (async () => {
        const { data } = await loadOptions(valueDebounce);
        if (active) setOptions(data);
      })();

      return () => {
        active = false;
      };
    }, [open, valueDebounce]);

    return (
      <Autocomplete
        multiple={multiple}
        style={style}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        value={multiple ? value || [] : value}
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          updateValue(newInputValue);
        }}
        getOptionSelected={getOptionSelected}
        getOptionLabel={getOptionLabel}
        options={options}
        loading={loading}
        fullWidth={fullWidth}
        autoFocus={autoFocus}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            margin="normal"
            variant="outlined"
            {...rest}
            {...params}
            label={label}
            fullWidth
            inputRef={forwardedRef}
            autoFocus={autoFocus}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  },
);

const AutoCompleteRemoto2 = forwardRef(
  (
    {
      value,
      onChange,
      loadOptions,
      label,
      campo = 'nome',
      autoFocus = false,
      multiple = false,
      ...rest
    },
    forwardedRef,
  ) => {
    const [inputValue, setInputValue] = useState('');
    const [valueDebounce, setValueDebounce] = useState('');

    useEffect(() => {
      const timer = setTimeout(() => setValueDebounce(inputValue), 500);
      return () => clearTimeout(timer);
    }, [inputValue]);

    return (
      <AutoCompleteBase
        inputValue={inputValue}
        onChange={(newValue) => {
          onChange(newValue);
        }}
        updateValue={(newValue) => setInputValue(newValue)}
        value={value}
        label={label}
        valueDebounce={valueDebounce}
        loadOptions={loadOptions}
        ref={forwardedRef}
        campo={campo}
        autoFocus={autoFocus}
        multiple={multiple}
        {...rest}
      />
    );
  },
);
function AutoCompleteRemoto({
  name,
  group = 'default',
  priority = 1,
  visible = true,
  deps = [],
  disabled = false,
  multiple = false,
  ...props
}) {
  return (
    <ControllerInput
      name={name}
      group={group}
      priority={priority}
      adapterValueOnChange={(value) => value}
      deps={deps}
      disabled={disabled}
      visible={visible}
      render={({ field }) => (
        <AutoCompleteRemoto2 {...props} {...field} multiple={multiple} />
      )}
    />
  );
}

export default AutoCompleteRemoto;
